/**
 * SEO head dedupe utility.
 *
 * Why: The build pipeline pre-renders route-specific SEO tags into
 * `/<route>/index.html`. Pages then mount react-helmet-async which appends
 * its OWN tags (title, description, canonical, robots, og:*, twitter:*).
 * Helmet only manages tags it created — pre-rendered tags stay → duplicates.
 *
 * Google Search Console live-test confirmed the duplicates affect crawler
 * rendering (two <title>, two meta description, etc.). This utility cleans
 * the head after Helmet has applied its changes so the rendered DOM has
 * exactly ONE active tag per managed type.
 *
 * Tags managed here (the contract from the bug report):
 *   • <title>
 *   • <meta name="description">
 *   • <meta name="robots">
 *   • <link rel="canonical">
 *   • <meta property="og:title">
 *   • <meta property="og:description">
 *   • <meta property="og:url">
 *   • <meta name="twitter:title">
 *   • <meta name="twitter:description">
 *
 * NOT touched: widget, GA4, Cloudflare scripts, fonts, manifest, icons,
 * structured-data JSON-LD, any meta the bug report didn't explicitly list.
 */

const SEO_SELECTORS = [
  'title',
  'meta[name="description"]',
  'meta[name="robots"]',
  'link[rel="canonical"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
];

function isHelmetOwned(node) {
  return (
    node.getAttribute('data-rh') === 'true' ||
    node.getAttribute('data-react-helmet') === 'true'
  );
}

/**
 * Dedupe rule (v2 — safe with react-helmet-async).
 *
 * The previous version chose a single winner across ALL duplicates,
 * sometimes deleting a helmet-managed tag. That created a race during
 * route transitions where react-helmet-async's internal cleanup hit a
 * node whose parentNode had been removed → `null.removeChild` → React
 * render cycle aborts → URL changes but page never re-renders.
 *
 * Strict rule now:
 *   • NEVER remove a node react-helmet-async manages (`data-rh="true"`).
 *     Let helmet handle its own lifecycle.
 *   • If at least one helmet-managed tag exists for this selector, all
 *     non-helmet copies (prerendered static tags) are removed.
 *   • If there are zero helmet-managed tags but multiple static copies,
 *     keep the last in DOM order and remove the rest.
 */
function dedupeSelector(selector) {
  const head = typeof document !== 'undefined' && document.head;
  if (!head) return 0;
  const nodes = Array.from(head.querySelectorAll(selector));
  if (nodes.length <= 1) return 0;

  const helmetNodes = nodes.filter(isHelmetOwned);
  const nonHelmetNodes = nodes.filter((n) => !isHelmetOwned(n));

  let toRemove;
  if (helmetNodes.length > 0) {
    // Helmet is in charge — clear out every static (prerendered) copy.
    toRemove = nonHelmetNodes;
  } else if (nonHelmetNodes.length > 1) {
    // No helmet copy at all — keep the LAST static one, remove others.
    toRemove = nonHelmetNodes.slice(0, -1);
  } else {
    toRemove = [];
  }

  let removed = 0;
  for (const node of toRemove) {
    // Belt-and-braces: parentNode can be null if a concurrent helmet
    // flush already detached the node.
    if (node.parentNode) {
      node.parentNode.removeChild(node);
      removed += 1;
    }
  }
  return removed;
}

/**
 * Sweep all SEO selectors and remove duplicates. Idempotent.
 * Returns the total number of removed nodes.
 */
export function dedupeSeoTags() {
  if (typeof document === 'undefined') return 0;
  let total = 0;
  for (const sel of SEO_SELECTORS) total += dedupeSelector(sel);
  return total;
}

/**
 * Upsert helpers (used by tests / explicit single-tag updates). React-Helmet
 * still owns insertion for normal page renders; these are escape hatches that
 * guarantee single-tag semantics if anything ever needs to bypass Helmet.
 */
export function upsertMeta(name, content) {
  if (typeof document === 'undefined' || !name) return null;
  let node = document.head.querySelector(`meta[name="${name}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute('name', name);
    document.head.appendChild(node);
  }
  node.setAttribute('content', content == null ? '' : String(content));
  return node;
}

export function upsertPropertyMeta(property, content) {
  if (typeof document === 'undefined' || !property) return null;
  let node = document.head.querySelector(`meta[property="${property}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute('property', property);
    document.head.appendChild(node);
  }
  node.setAttribute('content', content == null ? '' : String(content));
  return node;
}

export function upsertLinkRel(rel, href) {
  if (typeof document === 'undefined' || !rel) return null;
  let node = document.head.querySelector(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement('link');
    node.setAttribute('rel', rel);
    document.head.appendChild(node);
  }
  node.setAttribute('href', href == null ? '' : String(href));
  return node;
}

export function setSingleTitle(title) {
  if (typeof document === 'undefined') return;
  const titles = Array.from(document.head.querySelectorAll('title'));
  if (titles.length > 1) {
    for (const t of titles.slice(0, -1)) t.parentNode && t.parentNode.removeChild(t);
  }
  document.title = title || '';
}

// Exposed for unit tests
export const __SEO_SELECTORS = SEO_SELECTORS;

/**
 * Remove every STATIC (non-React) copy of the four route-specific SEO tags
 * — <title>, <meta name="description">, <meta name="robots"> and
 * <link rel="canonical"> — from <head> before React mounts.
 *
 * This covers BOTH:
 *   1. The prerender-injected tags marked `data-prerendered-seo="1"`
 *      (scripts/prerender-seo.js), and
 *   2. Any unmarked base tags shipped in public/index.html that survive
 *      when a route is served the raw SPA shell / DB-render fallback.
 *
 * Runs SYNCHRONOUSLY from src/index.js before ReactDOM.createRoot(), so at
 * this instant NOTHING in <head> is React/Helmet-owned yet — every such
 * node is static markup. Removing them all guarantees react-helmet-async
 * becomes the SOLE runtime owner of these four tags in every serving
 * scenario (prerender file, SPA shell or SSR fallback), i.e. exactly one of
 * each after hydration. Only the four managed types are touched — og:*,
 * twitter:*, charset, viewport, icons, manifest, JSON-LD and scripts are
 * left untouched. Raw HTML for no-JS crawlers is unaffected (this only
 * mutates the live client DOM and never runs without JS).
 */
const STATIC_SEO_SELECTORS = [
  'title',
  'meta[name="description"]',
  'meta[name="robots"]',
  'link[rel="canonical"]',
];

export function stripPrerenderedSeoTags() {
  if (typeof document === 'undefined' || !document.head) return 0;
  const selector = ['[data-prerendered-seo]', ...STATIC_SEO_SELECTORS].join(',');
  const nodes = document.head.querySelectorAll(selector);
  let removed = 0;
  for (const node of nodes) {
    if (node.parentNode) {
      node.parentNode.removeChild(node);
      removed += 1;
    }
  }
  return removed;
}
